import type { DbExecutor } from "./executor";
import { DEFAULT_WALL_HEIGHT_INCHES } from "../domain/room";
import { generateId } from "./ids";
import { parseStandardSnapshot } from "../domain/renovationStandard";
import type { AssemblyRepository } from "./repositories/assemblyRepository";
import type { FeatureRepository } from "./repositories/featureRepository";
import {
  FINISH_MATERIALS,
  materialByKey,
  materialsFor,
  removalAllowed,
  resolveFinishes,
  suggestedMaterial,
  type ExistingFinish,
  type FinishCoverage,
  type FinishProjectChoice,
  type FinishReconciliation,
  type FinishRoom,
  type FinishRoomOverride,
  type FinishSurface,
  type ResolvedRoomFinish,
} from "../engine/finishes";

/**
 * PROJECT FINISHES, APPLIED (spec §82).
 *
 * The engine decides coverage; this turns it into ORDINARY project scope
 * — one design-scoped feature per finish, carrying a geometry-derived
 * quantity and the seeded assembly that prices it. Everything downstream
 * (Scope & Estimate, assemblies, BOM, budget, bid sheet, quotes,
 * forecast) therefore works with no knowledge that finishes exist.
 *
 * ONE FEATURE PER FINISH, NOT ONE PER ROOM (spec §82-23): "LVP throughout"
 * is one decision and one line, and its quantity is the sum of the rooms
 * it covers. project_features.finish_key is the durable identity, so a
 * rename can never orphan it or make a second one appear.
 *
 * NOTHING HERE STORES A QUANTITY: every figure is recomputed from the
 * design's own geometry when it is asked for (§82-31).
 */

/** The definition + action each surface's scope is filed under. */
const SURFACE_SCOPE: Readonly<Record<FinishSurface, { definitionId: string; action: string; name: (display: string) => string }>> = {
  floor: { definitionId: "replace-flooring", action: "install", name: (d) => `Install ${d}` },
  // Instruction-shaped names, so the contractor sheet prints them as the
  // work they are rather than prefixing the design action (§79.1 wording).
  walls: { definitionId: "paint-walls", action: "refinish", name: () => "Paint Walls" },
  ceiling: { definitionId: "paint-ceiling", action: "refinish", name: () => "Paint Ceilings" },
};


/**
 * Scope that already covers the same surface room by room (spec §82-34).
 * Older projects were finished a room at a time — usually by a scope
 * package — and a whole-project finish laid on top of that would count
 * the same floor twice. These are REPORTED, never quietly removed: what
 * to do with somebody's existing scope is their decision.
 */
export interface FinishConflict {
  surface: FinishSurface;
  featureId: string;
  name: string;
  roomName: string | null;
  /** Money already recorded against it; anything > 0 is never removable. */
  actualCents: number;
}

export interface FinishModel {
  designId: string | null;
  conflicts: FinishConflict[];
  project: FinishProjectChoice[];
  overrides: FinishRoomOverride[];
  existing: ExistingFinish[];
  rooms: ResolvedRoomFinish[];
  coverage: FinishCoverage[];
  reconciliation: FinishReconciliation[];
  /** Rooms as the engine saw them, for the room-by-room drill-down. */
  measured: FinishRoom[];
}

interface RoomRow {
  geometry_room_id: string | null;
  id: string;
  name: string;
  room_type_id: string;
  ceiling_height_inches: number | null;
  default_ceiling_height_inches: number | null;
  manual_area_x100: number | null;
  manual_perimeter_x100: number | null;
  floor_area_source: string | null;
  perimeter_source: string | null;
  geometry_area_x100: number | null;
  geometry_perimeter_x100: number | null;
}

export class FinishService {
  private autoRemovable: ((featureId: string) => Promise<boolean>) | null = null;

  setAutoRemovableResolver(resolver: (featureId: string) => Promise<boolean>): void {
    this.autoRemovable = resolver;
  }
  constructor(
    private readonly db: DbExecutor,
    private readonly features: FeatureRepository,
    private readonly assemblies: AssemblyRepository,
  ) {}

  /** The rooms this project can finish, with their authoritative geometry. */
  private async roomsFor(projectId: string, designId: string | null): Promise<FinishRoom[]> {
    const rows = await this.db.select<RoomRow>(
      `SELECT r.id, r.name, r.room_type_id,
              r.ceiling_height_inches, f.default_ceiling_height_inches,
              r.floor_area_x100 AS manual_area_x100, r.perimeter_x100 AS manual_perimeter_x100,
              r.floor_area_source, r.perimeter_source,
              g.room_id AS geometry_room_id,
              g.floor_area_x100 AS geometry_area_x100, g.perimeter_x100 AS geometry_perimeter_x100
         FROM rooms r
         JOIN floors f ON f.id = r.floor_id
         LEFT JOIN design_room_geometry g ON g.room_id = r.id AND g.design_id = ?
        WHERE f.project_id = ? AND r.archived_at IS NULL AND f.archived_at IS NULL
        ORDER BY f.sort_order, r.sort_order, r.name`,
      [designId, projectId],
    );
    return rows.map((row) => ({
      roomId: row.id,
      roomName: row.name,
      roomTypeId: row.room_type_id,
      // Geometry is the authority; a hand measurement stands in where the
      // room has not been drawn (the Phase 7 effective-measurement rule).
      floorAreaX100:
        row.floor_area_source === "override" && row.manual_area_x100 !== null ? Number(row.manual_area_x100)
        : row.geometry_area_x100 !== null && row.geometry_area_x100 !== undefined
          ? Number(row.geometry_area_x100)
          : row.geometry_room_id !== null || row.manual_area_x100 === null || row.manual_area_x100 === undefined
            ? null
            : Number(row.manual_area_x100),
      perimeterX100:
        row.perimeter_source === "override" && row.manual_perimeter_x100 !== null ? Number(row.manual_perimeter_x100)
        : row.geometry_perimeter_x100 !== null && row.geometry_perimeter_x100 !== undefined
          ? Number(row.geometry_perimeter_x100)
          : row.geometry_room_id !== null || row.manual_perimeter_x100 === null || row.manual_perimeter_x100 === undefined
            ? null
            : Number(row.manual_perimeter_x100),
      wallHeightInches:
        row.ceiling_height_inches ?? row.default_ceiling_height_inches ?? DEFAULT_WALL_HEIGHT_INCHES,
    }));
  }

  /** Decisions + geometry + resolution, in a fixed number of statements. */
  async model(projectId: string, designId: string | null): Promise<FinishModel> {
    const [rooms, defaultRows, overrideRows, existingRows] = await Promise.all([
      this.roomsFor(projectId, designId),
      this.db.select<{ surface: string; material_key: string; enabled: number }>(
        `SELECT surface, material_key, enabled FROM project_finish_defaults
          WHERE project_id = ? AND (design_id IS NULL OR design_id = ?)
          ORDER BY CASE WHEN design_id IS NULL THEN 0 ELSE 1 END`,
        [projectId, designId],
      ),
      this.db.select<{ room_id: string; surface: string; material_key: string | null; excluded: number }>(
        `SELECT room_id, surface, material_key, excluded FROM room_finish_overrides
          WHERE project_id = ? AND (design_id IS NULL OR design_id = ?)
          ORDER BY CASE WHEN design_id IS NULL THEN 0 ELSE 1 END`,
        [projectId, designId],
      ),
      this.db.select<{ room_id: string; surface: string; material_key: string | null; provenance: string }>(
        "SELECT room_id, surface, material_key, provenance FROM room_existing_finishes WHERE project_id = ?",
        [projectId],
      ),
    ]);

    const project: FinishProjectChoice[] = defaultRows.map((r) => ({
      surface: r.surface as FinishSurface,
      materialKey: r.material_key,
      enabled: Number(r.enabled) === 1,
    }));
    const overrides: FinishRoomOverride[] = overrideRows.map((r) => ({
      roomId: r.room_id,
      surface: r.surface as FinishSurface,
      materialKey: r.material_key,
      excluded: Number(r.excluded) === 1,
    }));
    const existing: ExistingFinish[] = existingRows.map((r) => ({
      roomId: r.room_id,
      surface: r.surface as FinishSurface,
      materialKey: r.material_key,
      provenance: r.provenance === "user" ? "user" : "suggested",
    }));

    const conflicts = await this.conflictsFor(projectId, designId);
    const resolution = resolveFinishes({ rooms, project, overrides });
    return { designId, conflicts, project, overrides, existing, measured: rooms, ...resolution };
  }

  /** Room-by-room scope covering a surface a project finish also covers. */
  private async conflictsFor(projectId: string, designId: string | null): Promise<FinishConflict[]> {
    const rows = await this.db.select<{
      surface: string; id: string; name: string; room_name: string | null; cents: number | null;
    }>(
      `SELECT CASE f.feature_definition_id
                WHEN 'replace-flooring' THEN 'floor'
                WHEN 'paint-walls' THEN 'walls'
                ELSE 'ceiling' END AS surface,
              f.id, f.name, r.name AS room_name,
              (SELECT COALESCE(SUM(e.amount_cents), 0) FROM actual_expenses e WHERE e.feature_id = f.id) AS cents
         FROM project_features f
         LEFT JOIN rooms r ON r.id = f.room_id
        WHERE f.project_id = ? AND f.archived_at IS NULL AND f.finish_key IS NULL
          AND f.room_id IS NOT NULL
          AND f.feature_definition_id IN ('replace-flooring','paint-walls','paint-ceiling')
          AND (f.design_id IS NULL OR f.design_id = ?)
        ORDER BY f.sort_order`,
      [projectId, designId],
    );
    return rows.map((row) => ({
      surface: row.surface as FinishSurface,
      featureId: row.id,
      name: row.name,
      roomName: row.room_name,
      actualCents: Number(row.cents ?? 0),
    }));
  }

  /**
   * Retire room-by-room finish scope the project finish now covers
   * (spec §82-7). Explicit — never part of applying a finish — and it
   * never touches work that carries recorded money.
   */
  async replaceRoomScope(projectId: string, designId: string | null, surface: FinishSurface): Promise<number> {
    return this.db.transaction(async () => {
      const conflicts = (await this.conflictsFor(projectId, designId)).filter(
        (c) => c.surface === surface && c.actualCents === 0,
      );
      const now = new Date().toISOString();
      for (const c of conflicts) {
        await this.db.execute(
          "UPDATE project_features SET archived_at = ?, updated_at = ? WHERE id = ?",
          [now, now, c.featureId],
        );
      }
      return conflicts.length;
    });
  }

  /* ---------------- decisions ---------------- */

  /** The whole-project finish for one surface (spec §82-8). */
  async setProjectFinish(
    projectId: string,
    designId: string | null,
    surface: FinishSurface,
    materialKey: string | null,
  ): Promise<void> {
    const now = new Date().toISOString();
    const existing = await this.db.select<{ id: string }>(
      `SELECT id FROM project_finish_defaults WHERE project_id = ? AND (design_id IS ? OR design_id = ?) AND surface = ?`,
      [projectId, designId, designId, surface],
    );
    if (materialKey === null) {
      // Switched off, and the choice is forgotten rather than left to
      // surprise somebody later.
      if (existing.length > 0) {
        await this.db.execute("DELETE FROM project_finish_defaults WHERE id = ?", [existing[0].id]);
      }
      return;
    }
    if (materialByKey(materialKey) === null) throw new Error(`Unknown finish "${materialKey}".`);
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO project_finish_defaults (id, project_id, design_id, surface, material_key, enabled, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, 1, ?, ?)`,
        [generateId(), projectId, designId, surface, materialKey, now, now],
      );
      return;
    }
    await this.db.execute(
      "UPDATE project_finish_defaults SET material_key = ?, enabled = 1, updated_at = ? WHERE id = ?",
      [materialKey, now, existing[0].id],
    );
  }

  /** One room's exception (spec §82-11). Null clears it back to the default. */
  async setRoomOverride(
    projectId: string,
    designId: string | null,
    roomId: string,
    surface: FinishSurface,
    value: { materialKey: string | null; excluded: boolean } | null,
  ): Promise<void> {
    const now = new Date().toISOString();
    const existing = await this.db.select<{ id: string }>(
      `SELECT id FROM room_finish_overrides
        WHERE project_id = ? AND (design_id IS ? OR design_id = ?) AND room_id = ? AND surface = ?`,
      [projectId, designId, designId, roomId, surface],
    );
    if (value === null) {
      if (existing.length > 0) await this.db.execute("DELETE FROM room_finish_overrides WHERE id = ?", [existing[0].id]);
      return;
    }
    if (value.materialKey !== null && materialByKey(value.materialKey) === null) {
      throw new Error(`Unknown finish "${value.materialKey}".`);
    }
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO room_finish_overrides (id, project_id, design_id, room_id, surface, material_key, excluded, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [generateId(), projectId, designId, roomId, surface, value.materialKey, value.excluded ? 1 : 0, now, now],
      );
      return;
    }
    await this.db.execute(
      "UPDATE room_finish_overrides SET material_key = ?, excluded = ?, updated_at = ? WHERE id = ?",
      [value.materialKey, value.excluded ? 1 : 0, now, existing[0].id],
    );
  }

  /** Several rooms at once (spec §82-13). */
  async setRoomOverrides(
    projectId: string,
    designId: string | null,
    roomIds: readonly string[],
    surface: FinishSurface,
    value: { materialKey: string | null; excluded: boolean } | null,
  ): Promise<void> {
    await this.db.transaction(async () => {
      for (const roomId of roomIds) await this.setRoomOverride(projectId, designId, roomId, surface, value);
    });
  }

  /**
   * What is on the floor NOW, and who says so (spec §82-3/4). Confirming
   * it is what permits its removal to be priced; a suggestion never does.
   */
  async setExistingFinish(
    projectId: string,
    roomId: string,
    surface: FinishSurface,
    materialKey: string | null,
    provenance: "suggested" | "user",
  ): Promise<void> {
    const now = new Date().toISOString();
    const existing = await this.db.select<{ id: string }>(
      "SELECT id FROM room_existing_finishes WHERE project_id = ? AND room_id = ? AND surface = ?",
      [projectId, roomId, surface],
    );
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO room_existing_finishes (id, project_id, room_id, surface, material_key, provenance, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [generateId(), projectId, roomId, surface, materialKey, provenance, now, now],
      );
      return;
    }
    await this.db.execute(
      "UPDATE room_existing_finishes SET material_key = ?, provenance = ?, updated_at = ? WHERE id = ?",
      [materialKey, provenance, now, existing[0].id],
    );
  }

  /** Berrier Reno's own guess for a room, always labelled as one. */
  async suggestExistingFor(projectId: string, roomId: string, roomTypeId: string): Promise<void> {
    const suggested = suggestedMaterial(roomTypeId, "floor");
    if (!suggested) return;
    await this.setExistingFinish(projectId, roomId, "floor", suggested, "suggested");
  }

  /* ---------------- applying the decisions ---------------- */

  /** Committed edits only. Older snapshots never acquire new scope on opening. */
  async reconcileAutomatic(projectId: string, designId: string | null): Promise<string[]> {
    if (designId === null) return [];
    const rows = await this.db.select<{ renovation_standard_snapshot_json: string | null; kind: string }>(
      `SELECT p.renovation_standard_snapshot_json, d.kind FROM projects p
       JOIN designs d ON d.project_id = p.id WHERE p.id = ? AND d.id = ?`,
      [projectId, designId],
    );
    const row = rows[0];
    if (!row || row.kind !== "proposed" || parseStandardSnapshot(row.renovation_standard_snapshot_json)?.automaticFinishVersion !== 1) return [];
    return this.apply(projectId, designId, true);
  }

  /**
   * Materialise the finishes as ordinary scope. Idempotent: run it after
   * any decision or geometry change and the project's features match what
   * the engine says, without disturbing anything a person did.
   */
  async apply(projectId: string, designId: string | null, automatic = false): Promise<string[]> {
    return this.db.transaction(async () => {
      const model = await this.model(projectId, designId);
      // Legacy room/package scope remains authoritative. Report its existing
      // conflict rather than silently retiring it or stacking another surface.
      const blocked = new Set(automatic ? model.conflicts.map((c) => c.surface) : []);
      const existingByRoom = new Map(
        model.existing.filter((e) => e.surface === "floor").map((e) => [e.roomId, e]),
      );
      const touched: string[] = [];
      const wanted = new Map<string, FinishCoverage>();
      for (const c of model.coverage) {
        if (blocked.has(c.surface)) continue;
        if (automatic && !model.project.some((p) => p.surface === c.surface && p.enabled)) continue;
        if (c.areaX100 <= 0) continue;
        wanted.set(`${c.surface}:${c.materialKey}`, c);
      }

      const owned = await this.db.select<{ id: string; finish_key: string; archived_at: string | null; quantity_geometry_x100: number | null }>(
        `SELECT id, finish_key, archived_at, quantity_geometry_x100 FROM project_features
          WHERE project_id = ? AND finish_key IS NOT NULL AND (design_id IS ? OR design_id = ?)`,
        [projectId, designId, designId],
      );
      const ownedByKey = new Map(owned.map((row) => [row.finish_key, row]));

      for (const [key, coverage] of wanted) {
        const material = materialByKey(coverage.materialKey);
        if (!material) continue;
        const scope = SURFACE_SCOPE[coverage.surface];
        const current = ownedByKey.get(key);
        let featureId: string;
        if (current) {
          featureId = current.id;
          if (current.archived_at !== null) {
            await this.db.execute("UPDATE project_features SET archived_at = NULL, updated_at = ? WHERE id = ?", [
              new Date().toISOString(),
              featureId,
            ]);
          }
        } else {
          const created = await this.features.create(projectId, {
            designId,
            roomId: null,
            floorId: null,
            featureDefinitionId: scope.definitionId,
            name: scope.name(material.displayName),
            action: scope.action as never,
            quantityX100: null,
            quantityUnit: null,
            notes: "",
          });
          featureId = created.id;
          await this.db.execute(
            "UPDATE project_features SET finish_key = ?, quantity_geometry_binding = 'project-finish-area' WHERE id = ?",
            [key, featureId],
          );
        }

        // The quantity is the geometry's, always recomputed (§82-31).
        const quantityChanged = !current || current.quantity_geometry_x100 !== coverage.areaX100;
        if (quantityChanged) await this.features.setGeometryQuantity(featureId, coverage.areaX100);

        // One assembly, attached once — the ordinary estimating path.
        const instances = await this.assemblies.listByFeature(featureId);
        let instanceId = instances.find((i) => i.templateId === material.templateId)?.id ?? null;
        let attached = false;
        if (instanceId === null && instances.length === 0) {
          instanceId = (await this.assemblies.attachTemplate(featureId, material.templateId)).id;
          attached = true;
        }

        /*
         * THE SAFEGUARD (spec §82-4/20/21). A flooring assembly's tear-out
         * is switched on only where somebody CONFIRMED what is being taken
         * up. A suggestion — or silence — leaves it off: the installation
         * is priced, the demolition is not, and nobody is billed for a
         * guess.
         */
        if (instanceId !== null && material.removalOptionId !== null) {
          // The option applies to the WHOLE aggregated quantity. One confirmed
          // room cannot authorize tear-out in the remaining unknown rooms.
          const confirmed = coverage.roomIds.length > 0 && coverage.roomIds.every((roomId) => removalAllowed(existingByRoom.get(roomId)).allowed);
          const instance = await this.assemblies.getInstance(instanceId);
          const template = await this.assemblies.getTemplate(material.templateId);
          const prompt = template?.options.find((o) => o.id === material.removalOptionId)?.prompt;
          const option = instance?.options.find((o) => o.prompt === prompt);
          if (option) {
            const markers = await this.db.select<{ automatic_finish_default: number | null }>(
              "SELECT automatic_finish_default FROM assembly_instance_options WHERE id = ?", [option.id],
            );
            const prior = markers[0]?.automatic_finish_default;
            // NULL means legacy or explicitly edited. Public setOption clears
            // the marker, including when the person chose the same value.
            if (attached || (prior !== null && prior !== undefined && option.value === (Number(prior) === 1))) {
              if (option.value !== confirmed || attached) {
                await this.db.execute(
                  "UPDATE assembly_instance_options SET value = ?, automatic_finish_default = ? WHERE id = ?",
                  [confirmed ? 1 : 0, confirmed ? 1 : 0, option.id],
                );
                await this.assemblies.regenerateForFeature(featureId);
              }
            }
          }
        }
        if (quantityChanged || current?.archived_at !== null) await this.assemblies.regenerateForFeature(featureId);
        touched.push(featureId);
      }

      /*
       * A finish nobody chose any more goes away — but only when it is
       * still ours and untouched: anything carrying real money keeps its
       * scope and simply loses its quantity, exactly as the Phase 8
       * reconciliation rules require.
       */
      for (const [key, row] of ownedByKey) {
        if (blocked.has(key.split(":")[0] as FinishSurface)) continue;
        if (wanted.has(key) || row.archived_at !== null) continue;
        if (automatic && (!this.autoRemovable || !(await this.autoRemovable(row.id)))) {
          await this.features.setGeometryQuantity(row.id, null);
          await this.assemblies.regenerateForFeature(row.id);
          continue;
        }
        const money = await this.db.select<{ n: number }>(
          `SELECT (SELECT COUNT(*) FROM actual_expenses WHERE feature_id = ?)
                + (SELECT COUNT(*) FROM purchase_allocations WHERE linked_feature_id = ?) AS n`,
          [row.id, row.id],
        );
        if (Number(money[0]?.n ?? 0) > 0) {
          await this.features.setGeometryQuantity(row.id, null);
          continue;
        }
        await this.db.execute("UPDATE project_features SET archived_at = ?, updated_at = ? WHERE id = ?", [
          new Date().toISOString(),
          new Date().toISOString(),
          row.id,
        ]);
        touched.push(row.id);
      }
      return touched;
    });
  }

  /** Every finish a surface can take, for the pickers. */
  materials(surface: FinishSurface) {
    return materialsFor(surface);
  }

  allMaterials() {
    return FINISH_MATERIALS;
  }
}
